'use client';

import { useState, useEffect } from 'react';

const faqItems = [
  {
    question: "What is GhostHub OasiS?",
    answer: "A personal media hub that lets you watch your photos and videos anywhere. Browse in three different layouts (TikTok-style swipe, Netflix scroll, or photo gallery), cast to your TV, and share with family around the world. No accounts, no cloud, no monthly fees."
  },
  {
    question: "What's the difference between OasiS 3.0 and the free version?",
    answer: "GhostHub OasiS (3.0) includes major capabilities not available in the free version:\n\n• Three viewing modes (TikTok, Netflix, Gallery)\n• TV casting with phone remote control\n• Continue watching (remembers your place)\n• Watch parties with real-time sync\n• Secure remote access via mesh networking\n• Drag-and-drop file uploads\n• Hardware that's ready to go out of the box\n\nThe free open source version (v1.6) has basic streaming but requires technical setup and doesn't include these features."
  },
  {
    question: "Do I need technical skills?",
    answer: "No. Plug it in, connect to your network, and you're done. Everything is pre-configured. If you can plug in a TV, you can use GhostHub OasiS."
  },
  {
    question: "What file formats work?",
    answer: "Standard formats: MP4 for videos, JPG/PNG/WebP for images. Other browser-compatible formats also work."
  },
  {
    question: "How does TV Casting work?",
    answer: "Connect GhostHub OasiS to your TV via HDMI. It shows a waiting screen. Then from your phone, browse to any video and tap Cast — it plays on the TV. You control playback, seek, and volume from your phone like a remote."
  },
  {
    question: "Can I share with people outside my home?",
    answer: "Yes. GhostHub OasiS offers three ways to access your media remotely:\n\n• Headscale/Tailscale Mesh (Most Private) - Zero-trust WireGuard tunneling. Users install Tailscale and follow simple setup instructions shown in GhostHub. Maximum privacy - your data never goes through third-party servers.\n\n• Cloudflare Tunnel (Easy Sharing) - Creates a public URL you can share with anyone. Simple browser-only access, but traffic routes through Cloudflare servers. Great for quick sharing when privacy is less critical.\n\n• Pinggy (Alternative) - Another HTTP tunnel option similar to Cloudflare.\n\nChoose mesh for sensitive content with family/friends. Choose HTTP tunnels for convenience when privacy is less critical. No port forwarding required for any option."
  },
  {
    question: "What are watch parties?",
    answer: "Watch parties let multiple people watch the same content in perfect sync. One person becomes the host and controls what plays — everyone else follows along in real-time. Great for watching together with family across different locations."
  },
  {
    question: "Is my data private?",
    answer: "Completely. Your files never leave your device. There's no cloud, no tracking, no accounts. You control who sees what. Remote access uses secure, encrypted mesh networking."
  }
];

export default function FAQ() {
  const [activeIndex, setActiveIndex] = useState(null);
  const [isClient, setIsClient] = useState(false);
  
  // Detect client-side rendering
  useEffect(() => {
    setIsClient(true);
  }, []);

  const toggleFAQ = (index) => {
    if (!isClient) return; // Only toggle if we're on the client
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div className="bg-dark-900 py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">Frequently Asked Questions</h2>
          <p className="mt-6 text-lg leading-8 text-gray-300">
            Everything you need to know about GhostHub
          </p>
        </div>
        <div className="mx-auto mt-16 max-w-3xl divide-y divide-gray-700">
          <dl className="space-y-6 divide-y divide-gray-700">
            {faqItems.map((item, index) => (
              <div key={index} className="pt-6">
                <dt>
                  <button
                    type="button"
                    className="flex w-full items-start justify-between text-left text-white"
                    onClick={() => toggleFAQ(index)}
                    aria-expanded={activeIndex === index}
                  >
                    <span className="text-lg font-semibold leading-7">{item.question}</span>
                    <span className="ml-6 flex h-7 items-center">
                      <svg 
                        className={`h-6 w-6 transform ${activeIndex === index ? 'rotate-45' : ''} transition-transform duration-200`} 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        strokeWidth="1.5" 
                        stroke="currentColor" 
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
                      </svg>
                    </span>
                  </button>
                </dt>
                <dd 
                  className={`mt-2 pr-12 overflow-hidden transition-all duration-300 ease-in-out ${activeIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <p className="text-base leading-7 text-gray-300 whitespace-pre-line">
                    {item.answer}
                  </p>
                </dd>
              </div>
            ))}
          </dl>
          <div className="mt-10 pt-6 text-center">
            <p className="text-base text-gray-400">
              Can't find the answer you're looking for? Check out my{' '}
              <a href="/support" className="font-semibold text-ghost-400 hover:text-ghost-300">
                Support Page
              </a>{' '}
              for more help.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
